/**
 * Zentraler Zustand der App (Zustand-Store).
 *
 * Alles Veränderbare gehört der angemeldeten Person, je Haus-Zustand in zwei getrennten Dokumenten:
 *  - Einrichtung (nur Planung): Möbel, eigene Möbel
 *  - Leitungsplan: Leitungen, Anschlüsse
 * Beide sind unabhängig wählbar und werden getrennt gespeichert; ein gemeinsamer Rückgängig-Verlauf.
 * Das Gebäude ist für alle gleich (Code).
 *
 * Bearbeiten geht nur im Bearbeiten-Modus (Burger-Menü); sonst ist alles nur zum Ansehen.
 */
import { create } from "zustand";
import type { CustomDef, HouseKey, LayoutData, LayoutKind, LayoutSummary, Line, LineType, Me, Point, PointType } from "@hausplaner/model";
import { LAYOUT_VERSION, LayoutDataSchema, ItemSchema, CustomDefSchema, LineSchema, PointSchema, houseInfo, LINE_TYPES } from "@hausplaner/model";
import { FLOORS, floorBase } from "../engine/constants";
import { CATALOG, GARAGE_IDS, defOf, type CatalogDef } from "../engine/catalog";
import { Placement, type PlacedItem } from "../engine/placement";
import { forgetCustomThumbs, forgetThumb } from "../engine/thumbs";
import { POINT_INFO } from "../engine/lines";
import { getHouse } from "../scene/house";
import { centerPlanOn } from "../scene/rig";

export type Mode = "orbit" | "plan" | "walk";
export type FloorSel = "all" | 0 | 1 | 2 | 3;
export type Sheet = null | "catalog" | "builder";
export type Tool = null | "lines";
export interface Prefs { grid: number; magnet: boolean; guides: boolean; coll: boolean }
export type SaveState = "local" | "saving" | "saved" | "error" | "conflict" | "readonly" | "auth";
export type Layers = { roof: boolean; neighbour: boolean; labels: boolean; furniture: boolean } & Record<LineType, boolean>;
export interface LineOpts { kind: "line" | "point"; t: LineType; pt: PointType; z: number | "ceil" }
export type LineSel = { kind: "line" | "point"; id: string } | null;

const r3 = (v: number) => +(+v).toFixed(3);
let idSeq = 0;
const newId = (p = "i") => p + (++idSeq).toString(36) + Math.random().toString(36).slice(2, 6);

/* ---------- Einstellungen pro Browser ---------- */
function loadLS<T>(key: string, d: T): T { try { return { ...d, ...JSON.parse(localStorage.getItem(key) || "{}") }; } catch { return d; } }
function saveLS(key: string, v: unknown) { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* egal */ } }
const PREFS_KEY = "rosenstr30-platzieren", LAYERS_KEY = "hausplaner-ebenen";
const defaultLayers: Layers = { roof: true, neighbour: true, labels: true, furniture: true, ...Object.fromEntries(LINE_TYPES.map((t) => [t, true])) as Record<LineType, boolean> };

export interface State {
  /* Haus */
  houseKey: HouseKey | null;
  edit: boolean;

  /* Einrichtung (nur Planung) */
  items: PlacedItem[];
  custom: CustomDef[];
  bad: Set<string>;
  selectedId: string | null;

  /* Leitungsplan */
  lines: Line[];
  points: Point[];

  /* Leitungs-Werkzeug */
  tool: Tool;
  lineOpts: LineOpts;
  draft: [number, number, number][];
  /** Geschoss der Leitung, die gerade gezeichnet wird */
  draftF: number;
  lineSel: LineSel;

  /* Ansicht */
  mode: Mode;
  floor: FloorSel;
  layers: Layers;
  measure: boolean;
  measurePts: [number, number, number][];
  prefs: Prefs;

  /* Oberfläche */
  menu: boolean;
  sheet: Sheet;
  builderKey: string | null; // null = neues Möbel
  toast: { text: string; n: number } | null;
  hist: { canUndo: boolean; canRedo: boolean };

  /* Server */
  me: Me | null;
  /** Anmeldung nötig (beim Start oder weil die Sitzung abgelaufen ist) */
  needLogin: boolean;
  /** Start-Prüfung (angemeldet? Server da?) abgeschlossen – vorher wird nichts angezeigt */
  authChecked: boolean;
  online: boolean;
  /** geöffnete Einrichtung bzw. geöffneter Leitungsplan */
  docs: Record<LayoutKind, DocSlot>;
  /** eigene Einrichtungen bzw. Leitungspläne dieses Haus-Zustands */
  lists: Record<LayoutKind, LayoutSummary[]>;
  /** im Browser liegen Änderungen, die der Server nie bestätigt hat (z. B. Sitzung lief ab) */
  restore: Partial<Record<LayoutKind, LayoutData>> | null;
}
export interface DocSlot { id: string | null; name: string; version: number; save: SaveState }
const emptySlot = (save: SaveState): DocSlot => ({ id: null, name: "", version: 0, save });
const emptyDocs = (save: SaveState): Record<LayoutKind, DocSlot> => ({ einrichtung: emptySlot(save), leitungen: emptySlot(save) });

const initial: State = {
  houseKey: null, edit: false,
  items: [], custom: [], bad: new Set(), selectedId: null,
  lines: [], points: [],
  tool: null, lineOpts: { kind: "line", t: "strom", pt: "steckdose", z: 0.3 }, draft: [], draftF: 1, lineSel: null,
  mode: "orbit", floor: "all", layers: loadLS(LAYERS_KEY, defaultLayers), measure: false, measurePts: [],
  prefs: loadLS<Prefs>(PREFS_KEY, { grid: 0.05, magnet: true, guides: true, coll: true }),
  menu: false, sheet: null, builderKey: null, toast: null, hist: { canUndo: false, canRedo: false },
  me: null, needLogin: false, authChecked: false, online: false,
  docs: emptyDocs("local"), lists: { einrichtung: [], leitungen: [] }, restore: null,
};

export const useStore = create<State>()(() => initial);
const get = useStore.getState;
const set = useStore.setState;

/* ---------- Hilfen ---------- */
export const furnitureAllowed = (s: State = get()) => !!s.houseKey && houseInfo(s.houseKey).furniture;
/** welche Dokumente es in diesem Haus-Zustand gibt (Bestand: nur Leitungspläne) */
export const kindsFor = (key: HouseKey | null): LayoutKind[] => (key && houseInfo(key).furniture ? ["einrichtung", "leitungen"] : ["leitungen"]);
export const KIND_LABEL: Record<LayoutKind, { one: string; many: string; first: string }> = {
  einrichtung: { one: "Einrichtung", many: "Einrichtungen", first: "Meine Einrichtung" },
  leitungen: { one: "Leitungsplan", many: "Leitungspläne", first: "Meine Leitungen" },
};
const SEVERITY: SaveState[] = ["auth", "conflict", "error", "saving", "readonly", "local", "saved"];
/** Gesamt-Speicherstand aller offenen Dokumente (der „schlimmste“ gewinnt) */
export function saveStateOf(s: State = get()): SaveState {
  const states = kindsFor(s.houseKey).map((k) => s.docs[k].save);
  return SEVERITY.find((x) => states.includes(x)) || "saved";
}
export const currentHouse = () => getHouse(get().houseKey || "bestand");
export const selectedItem = (s: State = get()) => (s.selectedId ? s.items.find((i) => i.id === s.selectedId) || null : null);
export const placement = (items = get().items) => new Placement(currentHouse().colliders, items);
export const defFor = (k: string) => defOf(k, get().custom);
export const floorIdx = (s: State = get()): number | null => (s.floor === "all" ? null : s.floor);
/** Deckenhöhe für „unter Decke“ */
export const ceilZ = (f: number) => Math.max(0.3, FLOORS[f].h - 0.25);

let toastTimer: ReturnType<typeof setTimeout> | undefined;
export function toast(text: string) {
  set({ toast: { text, n: Date.now() } });
  clearTimeout(toastTimer); toastTimer = setTimeout(() => set({ toast: null }), 2600);
}

function makeItem(spec: Partial<PlacedItem> & { k: string; x: number; y: number; f: number }, custom = get().custom): PlacedItem {
  const def = defOf(spec.k, custom);
  return {
    id: spec.id || newId(), k: def.key, type: def.id, name: def.name,
    x: spec.x, y: spec.y, f: spec.f, r: spec.r || 0,
    w: spec.w ?? def.w, d: spec.d ?? def.d, h: spec.h ?? def.h, c: spec.c || def.c,
    z: spec.z ?? def.e ?? 0, lock: !!spec.lock, on: !!def.on, flat: def.id === "rug",
  };
}

function computeBad(items: PlacedItem[], coll: boolean): Set<string> {
  const out = new Set<string>(); if (!coll || !items.length) return out;
  const P = placement(items);
  for (const it of items) if (P.collides(it)) out.add(it.id);
  return out;
}

/** Leitungen und Anschlüsse bearbeiten erlaubt? (Bearbeiten-Modus) */
function canEditHouse(quiet = false): boolean {
  if (!get().edit) { if (!quiet) toast("Zum Bearbeiten im Menü den Bearbeiten-Modus einschalten"); return false; }
  return true;
}
/** Möbel bearbeiten erlaubt? (Bearbeiten-Modus, Planung, nicht fixiert) */
function canEdit(it?: PlacedItem | null, quiet = false): boolean {
  if (!canEditHouse(quiet)) return false;
  if (!furnitureAllowed()) { if (!quiet) toast("Möbel gibt es nur in der Planung"); return false; }
  if (it && it.lock) { if (!quiet) toast("Fixiert – zum Bearbeiten erst lösen"); return false; }
  return true;
}
export const isReadOnly = () => !get().edit;
export const canDragItems = () => get().edit && furnitureAllowed();

/* ---------- Serialisieren ---------- */
/** alles zusammen (für Export und Rückgängig-Verlauf) */
export function serialize(s: State = get()): LayoutData {
  return {
    v: LAYOUT_VERSION,
    items: s.items.map((i) => {
      const o: LayoutData["items"][number] = { k: i.k, x: r3(i.x), y: r3(i.y), f: i.f, r: +i.r.toFixed(4), w: r3(i.w), d: r3(i.d), h: r3(i.h), c: i.c };
      if (i.z) o.z = r3(i.z); if (i.lock) o.lock = 1; return o;
    }),
    custom: s.custom, lines: s.lines, points: s.points,
  };
}
/** nur der Teil einer Art (zum Speichern) */
export function serializeKind(kind: LayoutKind, s: State = get()): LayoutData {
  const all = serialize(s);
  return kind === "einrichtung"
    ? { v: all.v, items: all.items, custom: all.custom, lines: [], points: [] }
    : { v: all.v, items: [], custom: [], lines: all.lines, points: all.points };
}

/* ---------- Ändern + Rückgängig ---------- */
let hist: string[] = [], hptr = -1;
let histTimer: ReturnType<typeof setTimeout> | undefined;
const histState = () => JSON.stringify(serialize());
function commitHist() {
  histTimer = undefined;
  const s = histState(); if (hist[hptr] === s) return;
  hist = hist.slice(0, hptr + 1); hist.push(s); if (hist.length > 120) hist.shift(); hptr = hist.length - 1; updHist();
}
export function resetHist() { hist = [histState()]; hptr = 0; updHist(); }
function updHist() { set({ hist: { canUndo: hptr > 0, canRedo: hptr < hist.length - 1 } }); }

/** nach jeder inhaltlichen Änderung: Warnungen neu, Verlauf und Speichern anstoßen */
function commit(patch: Partial<State>) {
  const next = { ...get(), ...patch };
  set({ ...patch, bad: computeBad(next.items, next.prefs.coll) });
  clearTimeout(histTimer); histTimer = setTimeout(commitHist, 300);
  persistSoon();
}
function applyHist(json: string) { applyData(JSON.parse(json)); }
export function undo() {
  if (!get().edit) return;
  if (histTimer) { clearTimeout(histTimer); commitHist(); }
  if (hptr <= 0) return; hptr--; applyHist(hist[hptr]); persistSoon(); updHist(); toast("Rückgängig gemacht");
}
export function redo() {
  if (!get().edit) return;
  if (hptr >= hist.length - 1) return; hptr++; applyHist(hist[hptr]); persistSoon(); updHist(); toast("Wiederhergestellt");
}

/**
 * Daten laden (aus Datei, Server, Verlauf). Ungültiges wird verworfen statt abzustürzen.
 * only: nur die Einrichtung bzw. nur den Leitungsplan übernehmen, der andere Teil bleibt.
 */
export function applyData(raw: unknown, opts: { keepCustom?: boolean; only?: LayoutKind } = {}) {
  const parsed = LayoutDataSchema.safeParse(raw);
  const s = get();
  let data: LayoutData;
  if (parsed.success) data = parsed.data;
  else {
    // tolerant: einzelne kaputte Einträge überspringen
    const r = (raw || {}) as Partial<LayoutData>;
    data = { items: [], custom: [], lines: [], points: [] };
    for (const it of Array.isArray(r.items) ? r.items : []) { const p = ItemSchema.safeParse(it); if (p.success) data.items.push(p.data); }
    for (const c of Array.isArray(r.custom) ? r.custom : []) { const p = CustomDefSchema.safeParse(c); if (p.success) data.custom.push(p.data); }
    for (const l of Array.isArray(r.lines) ? r.lines : []) { const p = LineSchema.safeParse(l); if (p.success) data.lines.push(p.data); }
    for (const q of Array.isArray(r.points) ? r.points : []) { const p = PointSchema.safeParse(q); if (p.success) data.points.push(p.data); }
  }
  const patch: Partial<State> = {};
  if (opts.only !== "leitungen") {
    const custom = opts.keepCustom && !data.custom.length ? s.custom : data.custom.map((c) => ({ ...c, name: String(c.name || "Eigenes Möbel").slice(0, 40) }));
    forgetCustomThumbs();
    // Möbel gibt es nur in der Planung
    const items = furnitureAllowed(s) ? data.items.map((i) => makeItem({ ...i, lock: !!i.lock }, custom)) : [];
    Object.assign(patch, { items, custom, selectedId: null, bad: computeBad(items, s.prefs.coll) });
  }
  if (opts.only !== "einrichtung") Object.assign(patch, { lines: data.lines, points: data.points, lineSel: null, draft: [] });
  set(patch);
  return data;
}

/* ---------- Speichern: Server oder (ohne Backend) dieser Browser ---------- */
const LS = "rosenstr30-einrichtung";
let saveTimer: ReturnType<typeof setTimeout> | undefined;
let saving: Promise<unknown> = Promise.resolve();
const lastSaved: Record<LayoutKind, string> = { einrichtung: "", leitungen: "" };
/** merkt sich den zuletzt geladenen/gespeicherten Stand, damit nur Geändertes gespeichert wird */
export function markSaved(kind: LayoutKind, data: LayoutData = serializeKind(kind)) { lastSaved[kind] = JSON.stringify(data); }
function persistSoon() { clearTimeout(saveTimer); saveTimer = setTimeout(() => void doSave(), 700); }

async function doSave() {
  const s = get(); if (!s.houseKey) return;
  const { saveDoc } = await import("./layouts");
  for (const kind of kindsFor(s.houseKey)) {
    const data = serializeKind(kind, s), js = JSON.stringify(data);
    if (js === lastSaved[kind]) continue;
    const id = s.docs[kind].id, key = layoutKey(id, s.houseKey, kind);
    writeBackup(key, data, !s.online);
    if (s.online && id) saving = saving.then(async () => { if (await saveDoc(kind, data)) { lastSaved[kind] = js; writeBackup(key, data, true); } }).catch(() => undefined);
    else if (!s.online) lastSaved[kind] = js;
  }
  await saving;
}
/** nach erneuter Anmeldung: liegengebliebene Änderungen jetzt speichern */
export const retrySave = () => { void doSave(); };
export const flushSave = async () => { if (saveTimer) { clearTimeout(saveTimer); saveTimer = undefined; await doSave(); } await saving; };

/** Browser-Sicherung: je Einrichtung/Leitungsplan, ohne Server je Haus-Zustand und Art */
export const layoutKey = (id: string | null, house: HouseKey | null = get().houseKey, kind: LayoutKind = "einrichtung") =>
  LS + (id ? ":" + id : ":lokal:" + (house || "planung") + ":" + kind);
export function writeBackup(key: string, data: LayoutData, synced: boolean) {
  try { localStorage.setItem(key, JSON.stringify({ ...data, ts: Date.now(), synced })); } catch { /* egal */ }
}
export function loadBackup<T>(key: string): (T & { ts: number; synced?: boolean }) | null {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
export function dropBackup(key: string) { try { localStorage.removeItem(key); } catch { /* egal */ } }

/* ---------- Ansicht ---------- */
export function setMode(m: Mode) {
  const s = get();
  let floor = s.floor;
  if ((m === "plan" || s.tool) && floor === "all") floor = 1;
  set({ mode: m, floor, measurePts: [] });
}
export function setFloor(f: FloorSel) {
  const s = get();
  if ((s.mode === "plan" || s.tool) && f === "all") f = 1;
  set({ floor: f, measurePts: [], draft: [] });
}
export function toggleMeasure() {
  const s = get();
  if (s.measure) set({ measure: false, measurePts: [] });
  else { set({ measure: true, selectedId: null, measurePts: [], tool: null, draft: [], lineSel: null }); if (s.floor === "all") setFloor(1); }
}
const m2 = (v: number) => v.toFixed(2).replace(".", ",");
/** Messergebnis; bei Höhenunterschied zusätzlich senkrecht (↕) und waagerecht (↔) */
export function measureText(a: [number, number, number], b: [number, number, number]) {
  const h = Math.hypot(a[0] - b[0], a[2] - b[2]), v = Math.abs(a[1] - b[1]), d = Math.hypot(h, v);
  if (v < 0.03) return m2(d) + " m";
  if (h < 0.15) return "↕ " + m2(v) + " m"; // praktisch senkrecht (z. B. Boden an der Wand → Decke)
  return `${m2(d)} m · ↕ ${m2(v)} · ↔ ${m2(h)}`;
}
export function addMeasurePoint(p: [number, number, number]) {
  const pts = get().measurePts;
  const next = pts.length >= 2 ? [p] : [...pts, p];
  set({ measurePts: next });
  if (next.length === 2) {
    const [a, b] = next; const d = Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
    toast("Abstand: " + measureText(a, b));
  }
}
export function setPrefs(p: Partial<Prefs>) {
  const prefs = { ...get().prefs, ...p }; saveLS(PREFS_KEY, prefs);
  set({ prefs, bad: computeBad(get().items, prefs.coll) });
}
export function setLayer(k: keyof Layers, v: boolean) {
  const layers = { ...get().layers, [k]: v }; saveLS(LAYERS_KEY, layers); set({ layers });
}
export function openSheet(sheet: Sheet) {
  const s = get();
  const next = s.sheet === sheet ? null : sheet;
  set({ sheet: next, selectedId: next ? null : s.selectedId, menu: false });
}
export const closeSheet = () => set({ sheet: null });
export const setMenu = (open: boolean) => set(open ? { menu: true, sheet: null } : { menu: false });

/** Bearbeiten-Modus an/aus; beim Ausschalten wird sofort gespeichert */
export function setEdit(on: boolean) {
  if (on === get().edit) return;
  if (!on) { void flushSave(); set({ edit: false, tool: null, draft: [], lineSel: null, sheet: null, selectedId: null }); toast("Bearbeiten beendet – alles gespeichert"); }
  else { set({ edit: true, menu: false }); toast(furnitureAllowed() ? "Bearbeiten: deine Möbel und Leitungen" : "Bearbeiten: deine Leitungen"); }
}

/* ---------- Auswahl & Möbel bearbeiten ---------- */
export function select(id: string | null) {
  set(id ? { selectedId: id, sheet: null, lineSel: null } : { selectedId: null });
}

function updateItem(id: string, patch: Partial<PlacedItem>) {
  const items = get().items.map((i) => (i.id === id ? { ...i, ...patch } : i));
  commit({ items });
}

/** während des Ziehens: nur Position, ohne Verlauf/Speichern (das passiert am Ende) */
export function dragTo(id: string, x: number, y: number) {
  const s = get(); const it = s.items.find((i) => i.id === id); if (!it) return;
  const moving = { ...it, x, y };
  const P = placement(s.items);
  const z = it.on ? P.stackHeight(moving, x, y) : it.z;
  const items = s.items.map((i) => (i.id === id ? { ...moving, z } : i));
  const bad = new Set(s.bad);
  if (s.prefs.coll && placement(items).collides({ ...moving, z })) bad.add(id); else bad.delete(id);
  set({ items, bad });
}
export function endDrag() { commit({}); }
/** Änderung von außen (z. B. Import) als Schritt übernehmen: Verlauf + Speichern */
export function touch() { commit({}); }

export function setItemProps(patch: Partial<Pick<PlacedItem, "w" | "d" | "h" | "x" | "y" | "z" | "r" | "c">>) {
  const it = selectedItem(); if (!it || !canEdit(it)) return;
  const next = { ...it, ...patch };
  if (it.on && (patch.x !== undefined || patch.y !== undefined)) next.z = placement().stackHeight(next, next.x, next.y);
  updateItem(it.id, next);
}
export function rotateSel(a: number) {
  const it = selectedItem(); if (!it || !canEdit(it)) return;
  let r = it.r + a; if (Math.abs(Math.sin(2 * r)) < 1e-6) r = Math.round(r / (Math.PI / 2)) * (Math.PI / 2);
  updateItem(it.id, { r });
}
export function nudge(dx: number, dy: number) {
  const it = selectedItem(); if (!it || !canEdit(it)) return;
  const next = { ...it, x: +(it.x + dx).toFixed(3), y: +(it.y + dy).toFixed(3) };
  if (it.on) next.z = placement().stackHeight(next, next.x, next.y);
  updateItem(it.id, next);
}
export function alignToWall() {
  const it = selectedItem(); if (!it || !canEdit(it)) return;
  const res = placement().alignToWall(it);
  if (!res) return toast("Keine Wand in der Nähe gefunden");
  updateItem(it.id, res); toast("An die Wand gestellt");
}
export function moveToFloor(f: number) {
  const it = selectedItem(); if (!it || !canEdit(it)) return;
  let next = { ...it, f };
  const P = placement(get().items.filter((i) => i.id !== it.id));
  if (next.on) next.z = P.stackHeight(next, next.x, next.y);
  const free = P.findFree(next); if (free) next = { ...next, x: free[0], y: free[1] };
  updateItem(it.id, next);
  const s = get();
  if (s.mode !== "walk" && s.floor !== "all" && s.floor !== f) setFloor(f as FloorSel);
  toast(`${it.name} ins ${FLOORS[f].name} verschoben`);
}
export function toggleLock(id = get().selectedId) {
  if (!id || !canEdit(null)) return;
  const it = get().items.find((i) => i.id === id); if (!it) return;
  updateItem(id, { lock: !it.lock });
  toast(!it.lock ? `${it.name} fixiert` : `${it.name} gelöst`);
}
export function lockAll(lock: boolean) {
  if (!canEdit(null)) return;
  commit({ items: get().items.map((i) => ({ ...i, lock })) });
  toast(lock ? `Alle ${get().items.length} Möbel fixiert` : "Alle Möbel gelöst");
}
export function duplicateSel() {
  const it = selectedItem(); if (!it || !canEdit(null)) return;
  let n = makeItem({ ...it, id: undefined, lock: false, x: +(it.x + 0.3).toFixed(3), y: +(it.y - 0.3).toFixed(3) });
  const free = placement().findFree(n); if (free) n = { ...n, x: free[0], y: free[1] };
  commit({ items: [...get().items, n], selectedId: n.id });
}
export function deleteSel() {
  const it = selectedItem(); if (!it || !canEdit(it)) return;
  commit({ items: get().items.filter((i) => i.id !== it.id), selectedId: null });
  toast(it.name + " entfernt");
}
export function clearAll() {
  if (!canEdit(null)) return;
  commit({ items: [], selectedId: null });
  toast("Alle Möbel entfernt – mit „Rückgängig“ zurückholen");
}

/** Katalogeintrag ins Haus setzen. pos = Blickpunkt (Rundgang) oder Bildmitte (sonst). */
export function addFromCatalog(c: CatalogDef, pos: { f: number; x: number; y: number; walking: boolean }) {
  if (!canEdit(null)) return;
  let { f, x, y } = pos;
  const garden = c.cat === "Garten & Terrasse", garage = !!GARAGE_IDS[c.id];
  if (!pos.walking) {
    if (garage) { f = 0; x = -1.7; y = 5.6; }
    else if (garden) { f = 1; if (!(y > 10.9 || x < -0.1)) { x = 3.5; y = 14.2; } }
    const s = get(); if (s.floor === "all" || s.floor !== f) setFloor(f as FloorSel);
  }
  if (!garden && !garage) { x = Math.max(0.5, Math.min(7.2, x)); y = Math.max(0.6, Math.min(10.4, y)); }
  let it = makeItem({ k: c.key, x: Math.round(x * 20) / 20, y: Math.round(y * 20) / 20, f, r: 0 });
  const P = placement();
  if (it.on) it = { ...it, z: P.stackHeight(it, it.x, it.y) };
  const free = P.findFree(it); if (free) it = { ...it, x: free[0], y: free[1] };
  commit({ items: [...get().items, it], selectedId: it.id, sheet: null });
  toast(free ? `${c.name} im ${FLOORS[f].name} platziert – zum Verschieben ziehen` : `${c.name} platziert – kein freier Platz in der Nähe, bitte verschieben`);
}

/* ---------- Eigene Möbel ---------- */
export function openBuilder(key: string | null) { set({ sheet: "builder", builderKey: key, selectedId: null }); }
/** speichert ein eigenes Möbel; liefert den Katalogeintrag (neu: zum Platzieren) */
export function saveCustom(def: Omit<CustomDef, "key">, key: string | null): CatalogDef | null {
  if (!canEdit(null)) return null;
  const s = get();
  const old = key ? s.custom.find((c) => c.key === key) : undefined;
  if (old) {
    const nd: CustomDef = { ...def, key: old.key };
    const near = (a: number, b: number) => Math.abs(a - b) < 0.002;
    const custom = s.custom.map((c) => (c.key === old.key ? nd : c));
    const items = s.items.map((it) => {
      if (it.k !== old.key) return it;
      const n = { ...it, name: nd.name };
      if (near(it.w, old.w) && near(it.d, old.d) && near(it.h, old.h)) { n.w = nd.w; n.d = nd.d; n.h = nd.h; }
      if (it.c === old.c) n.c = nd.c;
      return n;
    });
    forgetThumb(old.key);
    commit({ custom, items, sheet: null });
    toast(nd.name + " aktualisiert");
    return null;
  }
  const nd: CustomDef = { ...def, key: "cu:" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6) };
  set({ custom: [...s.custom, nd], sheet: null });
  return defOf(nd.key, get().custom);
}
export function deleteCustom(key: string) {
  if (!canEdit(null)) return;
  const s = get();
  commit({ custom: s.custom.filter((c) => c.key !== key), items: s.items.filter((i) => i.k !== key), selectedId: null, sheet: null });
  toast("Eigenes Möbel gelöscht");
}

/* ---------- Leitungen ---------- */
export function setTool(tool: Tool) {
  const s = get();
  if (tool && !canEditHouse()) return;
  if (tool === s.tool) tool = null;
  const patch: Partial<State> = { tool, draft: [], lineSel: null, selectedId: null, measure: false, measurePts: [], sheet: null };
  if (tool && s.floor === "all") patch.floor = 1;
  // auf breiten Bildschirmen liegt das Panel links über dem Grundriss → Haus etwas nach rechts rücken
  if (tool && s.mode === "plan" && typeof window !== "undefined" && window.innerWidth >= 760) centerPlanOn(1.3, 5.5);
  if (tool) { const t = s.lineOpts.kind === "point" ? POINT_INFO[s.lineOpts.pt].layer : s.lineOpts.t; if (!s.layers[t]) setLayer(t, true); }
  set(patch);
}
export function setLineOpts(p: Partial<LineOpts>) {
  const prev = get().lineOpts;
  const lineOpts = { ...prev, ...p };
  set({ lineOpts });
  // Höhe während des Zeichnens geändert → an der letzten Stelle senkrecht hoch/runter
  if (p.z !== undefined && lineOpts.kind === "line" && get().draft.length && get().floor !== "all") riseTo(optHeight(get().draftF));
  const t = lineOpts.kind === "point" ? POINT_INFO[lineOpts.pt].layer : lineOpts.t;
  if (!get().layers[t]) setLayer(t, true);
}
/** Höhe für neue Punkte auf diesem Geschoss */
export function optHeight(f: number) { const z = get().lineOpts.z; return z === "ceil" ? ceilZ(f) : z; }

/** Klick beim Zeichnen: Punkt anhängen (mit Raster und rechten Winkeln) bzw. Anschluss setzen */
export function lineClick(x: number, y: number, free: boolean) {
  const s = get(); if (!canEditHouse() || s.floor === "all") return;
  const f = s.draft.length ? s.draftF : (s.floor as number), z = optHeight(f);
  const snap = (v: number) => Math.round(v / 0.05) * 0.05;
  addLinePoint(f, snap(x), snap(y), z, free);
}
/** Geschoss zu einer Höhe im Modell (Weltkoordinate) */
export function floorAt(wy: number) { let f = 0; FLOORS.forEach((fl, i) => { if (fl.z - 0.1 <= wy) f = i; }); return f; }
/** Klick in der 3D-Ansicht auf eine Wand, Decke oder einen Boden: Punkt genau dort (Höhe aus dem Treffer) */
export function lineClick3d(x: number, y: number, wy: number, free: boolean) {
  const s = get(); if (!canEditHouse()) return;
  const f = s.draft.length ? s.draftF : floorAt(wy);
  const z = Math.max(0, Math.round((wy - floorBase(f, x, y)) / 0.05) * 0.05);
  addLinePoint(f, Math.round(x * 100) / 100, Math.round(y * 100) / 100, z, free);
}
function addLinePoint(f: number, x: number, y: number, z: number, free: boolean) {
  const s = get();
  if (s.lineOpts.kind === "point") {
    const p: Point = { id: newId("p"), t: s.lineOpts.pt, f, x: r3(x), y: r3(y), z: r3(z) };
    commit({ points: [...s.points, p], lineSel: { kind: "point", id: p.id } });
    toast(POINT_INFO[p.t].name + " gesetzt");
    return;
  }
  const last = s.draft[s.draft.length - 1];
  if (last && !free) { // rechte Winkel: kleine Abweichungen zur Achse (auch senkrecht) werden begradigt
    const dx = Math.abs(x - last[0]), dy = Math.abs(y - last[1]), dz = Math.abs(z - last[2]), m = Math.max(dx, dy, dz);
    if (dx < m * 0.25) x = last[0];
    if (dy < m * 0.25) y = last[1];
    if (dz < m * 0.25) z = last[2];
  }
  if (last && Math.hypot(x - last[0], y - last[1]) < 0.02 && Math.abs(z - last[2]) < 0.01) { finishLine(); return; } // gleicher Punkt = fertig
  set({ draft: [...s.draft, [r3(x), r3(y), r3(z)]], draftF: f, lineSel: null });
}
/** senkrechtes Stück an der letzten Stelle; mehrfaches Umstellen der Höhe ändert dasselbe Stück */
function riseTo(z: number) {
  const d = get().draft; const last = d[d.length - 1]; if (!last) return;
  const prev = d[d.length - 2];
  const vertical = prev && prev[0] === last[0] && prev[1] === last[1];
  const base = vertical ? d.slice(0, -1) : d, from = base[base.length - 1];
  if (Math.abs(z - from[2]) < 0.01) { set({ draft: base }); return; }
  set({ draft: [...base, [from[0], from[1], r3(z)]] });
  toast(`Senkrecht ${z > from[2] ? "hoch" : "runter"} auf ${Math.round(z * 100)} cm – jetzt weiter im Grundriss tippen`);
}
export function undoDraftPoint() { set({ draft: get().draft.slice(0, -1) }); }
export function cancelDraft() { set({ draft: [] }); }
export function finishLine() {
  const s = get();
  if (s.draft.length < 2) { set({ draft: [] }); if (s.draft.length) toast("Eine Leitung braucht mindestens zwei Punkte"); return; }
  const l: Line = { id: newId("l"), t: s.lineOpts.t, f: s.draftF, pts: s.draft };
  commit({ lines: [...s.lines, l], draft: [], lineSel: { kind: "line", id: l.id } });
  toast(`Leitung gespeichert (${lineLength(l).toFixed(2).replace(".", ",")} m)`);
}
export const lineLength = (l: Pick<Line, "pts">) => l.pts.reduce((a, p, i) => (i ? a + Math.hypot(p[0] - l.pts[i - 1][0], p[1] - l.pts[i - 1][1], p[2] - l.pts[i - 1][2]) : 0), 0);
export function selectLine(sel: LineSel) { set({ lineSel: sel, selectedId: null }); }
export function updateLine(id: string, patch: Partial<Pick<Line, "t" | "note">> & { z?: number }) {
  if (!canEditHouse()) return;
  commit({ lines: get().lines.map((l) => {
    if (l.id !== id) return l;
    const n = { ...l, ...patch } as Line & { z?: number };
    if (patch.z !== undefined) { // alle Punkte gleicher Höhe verschieben (senkrechte Stücke bleiben relativ)
      const d = patch.z - l.pts[0][2]; n.pts = l.pts.map(([x, y, z]) => [x, y, r3(Math.max(0, z + d))] as [number, number, number]);
    }
    delete n.z; return n;
  }) });
}
export function updatePoint(id: string, patch: Partial<Pick<Point, "t" | "note" | "z">>) {
  if (!canEditHouse()) return;
  commit({ points: get().points.map((p) => (p.id === id ? { ...p, ...patch } : p)) });
}
export function deleteLineSel() {
  const s = get(); const sel = s.lineSel; if (!sel || !canEditHouse()) return;
  if (sel.kind === "line") commit({ lines: s.lines.filter((l) => l.id !== sel.id), lineSel: null });
  else commit({ points: s.points.filter((p) => p.id !== sel.id), lineSel: null });
  toast(sel.kind === "line" ? "Leitung gelöscht" : "Anschluss gelöscht");
}

/* ---------- Haus wechseln ---------- */
/** setzt alles Haus-Bezogene zurück (beim Wechsel zwischen Bestand/Planung/Startseite) */
export function resetHouseState(houseKey: HouseKey | null) {
  hist = []; hptr = -1; lastSaved.einrichtung = ""; lastSaved.leitungen = "";
  set({
    houseKey, edit: false, items: [], custom: [], bad: new Set(), selectedId: null,
    lines: [], points: [], tool: null, draft: [], lineSel: null,
    floor: "all", mode: "orbit", measure: false, measurePts: [], menu: false, sheet: null,
    docs: emptyDocs(get().online ? "saved" : "local"), lists: { einrichtung: [], leitungen: [] }, restore: null,
  });
  updHist();
}

export { CATALOG };
